import { Editor } from '@tinymce/tinymce-react';
import React, { FC, useRef } from 'react';

interface Props {
  content: string;
  onChange: (value: string) => void;
  editorRef?: React.MutableRefObject<any>;
  toolbar2?: string;
  allowLinks?: boolean;
}

const TinyEditorWrapper: FC<Props> = ({
  content,
  onChange,
  editorRef: externalEditorRef,
  toolbar2 = 'bold italic underline strikethrough superscript | removeformat | numlist bullist | indent outdent',
  allowLinks = false,
}) => {
  const internalEditorRef = useRef(null);
  const editorRef = externalEditorRef || internalEditorRef;

  function removeLinks(updatedContent: string) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = updatedContent;

    tempDiv.querySelectorAll('a').forEach(link => {
      if (!link.classList.contains('comment-anchor')) {
        const textNode = document.createTextNode(link.textContent);
        link.parentNode.replaceChild(textNode, link);
      }
    });

    return tempDiv.innerHTML;
  }

  return (
    <>
      <Editor
        tinymceScriptSrc={`${process.env.REACT_APP_DOMAIN}/tinymce/tinymce.min.js`}
        onInit={(_evt, editor) => {
          editorRef.current = editor;
          editor.on('BeforeSetContent', function (e) {
            e.content = e.content.replace(/<!--[\s\S]*?-->/g, '');
            if (!allowLinks) e.content = removeLinks(e.content);
          });

          editor.on('PostProcess', function (e) {
            if (e.set) {
              e.content = e.content.replace(/<!--[\s\S]*?-->/g, '');
              if (!allowLinks) e.content = removeLinks(e.content);
            }
          });
        }}
        onEditorChange={onChange}
        value={content}
        init={{
          height: 500,
          menubar: false,
          plugins: allowLinks ? 'lists link' : 'lists',
          branding: false,
          allow_html_in_named_anchor: true,
          toolbar1: allowLinks
            ? 'cut copy paste | undo redo | link'
            : 'cut copy paste | undo redo',
          toolbar2,
          browser_spellcheck: true,
          remove_trailing_brs: false,
          valid_elements: allowLinks
            ? `-a[id|name|class|style|href|target|rel],-strong/b,-em/i,-sup,-strike,-u,#p[style],-ul[type|compact|style],-ol[reversed|start|type|compact|style],#li[value|type|style],-s,br[clear],-span[style]`
            : `-a[id|name|class|style],-strong/b,-em/i,-sup,-strike,-u,#p[style],-ul[type|compact|style],-ol[reversed|start|type|compact|style],#li[value|type|style],-s,br[clear],-span[style]`,
          valid_styles: {
            '*': 'padding-left',
          },
          allow_conditional_comments: false,
          link_title: false,
          default_link_target: '_blank',
          link_target_list: false,
          anchor_top: false,
          anchor_bottom: false,
          formats: {
            underline: { inline: 'u', remove: 'all' },
          },
          setup: editor => {
            if (allowLinks) {
              editor.on('NodeChange', () => {
                editor
                  .getBody()
                  .querySelectorAll('a[href]')
                  .forEach(link => {
                    link.setAttribute('target', '_blank');
                    link.setAttribute('rel', 'noopener noreferrer');
                  });
              });
            }
          },
        }}
      />
    </>
  );
};

export default TinyEditorWrapper;
